import { PLACEHOLDER_IMAGE } from '@presentation/constants';
import { ConditionalWrapper } from '@presentation/utils';
import type { LocationDescriptor } from 'history';
import type { FC } from 'react';
import { Link } from 'react-router-dom';
import { Label, PlaceholderImage, SkeletonBox } from '..';
import * as Styled from './styled';
import type { CardItemProps } from './types';

export { CardItemContainer, CardItemWrapper } from './styled';
export * from './types';

export const CardItem: FC<CardItemProps> = (props) => {
  const {
    badges = [],
    content,
    footer,
    label,
    image,
    imageOverlay,
    imageButton,
    children,
    hideHoverOverlay = false,
    link,
    loading = false,
    linkWrapperProps,
    lazyLoadImage = true,
    useHeadingForTitle = true,
    shrinkHeightMobile = false,
    subtitleBadges,
    ...rest
  } = props;

  const hasTitle = !!content?.title;
  const hasSubtitle = !!content?.subtitle;
  const hasText = !!content?.text;

  const useH2ForSubtitle = !hasTitle && hasSubtitle && useHeadingForTitle;

  return (
    <ConditionalWrapper
      condition={!!link}
      wrapper={(wrapperChildren) => (
        <Link to={link as LocationDescriptor} {...linkWrapperProps}>
          {wrapperChildren}
        </Link>
      )}
    >
      <Styled.Card hideHoverOverlay={hideHoverOverlay} {...rest}>
        <Styled.Background shrinkHeightMobile={shrinkHeightMobile}>
          {!hideHoverOverlay && <Styled.CardLink />}
          {!!imageOverlay && <Styled.Overlay>{imageOverlay}</Styled.Overlay>}
          {!!imageButton && <Styled.ImageButton>{imageButton}</Styled.ImageButton>}
          {!!loading && (
            <SkeletonBox
              style={{
                height: 200,
                width: '100%',
              }}
            />
          )}
          {!loading ? (
            <>
              {lazyLoadImage ? (
                <Styled.LazyBackgroundImg
                  wrapperProps={{ style: { width: '100%' } }}
                  src={image ?? PLACEHOLDER_IMAGE}
                  effect="blur"
                  placeholder={<PlaceholderImage />}
                  alt="card-item-image"
                />
              ) : (
                <Styled.BackgroundImg src={image ?? PLACEHOLDER_IMAGE} alt="card-item-image" />
              )}
            </>
          ) : null}
        </Styled.Background>
        <Styled.Content hasBadge={!!badges.length || !!label}>
          {!!badges.length && (
            <Styled.BadgeContainer>
              {badges.map((badge, index) => (
                <Styled.BadgeWrapper key={index}>
                  <Styled.Badge label={badge.label} color={badge.color} expanded={badge.expanded}>
                    {badge.image && <img src={badge.image} alt="badge" />}
                    {!badge.image ? badge.icon : null}
                  </Styled.Badge>
                </Styled.BadgeWrapper>
              ))}
            </Styled.BadgeContainer>
          )}

          {!!label && (
            <Styled.LabelContainer>
              <Label {...label} />
            </Styled.LabelContainer>
          )}

          {!!content && (
            <>
              {hasTitle ? (
                <>
                  {useHeadingForTitle ? (
                    <Styled.ContentTitle>{content.title}</Styled.ContentTitle>
                  ) : (
                    <Styled.ContentTitleDiv>{content.title}</Styled.ContentTitleDiv>
                  )}
                </>
              ) : null}
              {hasSubtitle && (
                <>
                  {useH2ForSubtitle ? (
                    <Styled.ContentHeaderSubtitle>{content.subtitle}</Styled.ContentHeaderSubtitle>
                  ) : (
                    <Styled.ContentSubtitle>{content.subtitle}</Styled.ContentSubtitle>
                  )}
                </>
              )}
              {hasText && <Styled.ContentText>{content.text}</Styled.ContentText>}

              {subtitleBadges?.length ? (
                <Styled.SubtitleBadgeWrapper>
                  {subtitleBadges?.map((badge, index) => (
                    <Styled.SubtitleBadge
                      key={`${badge.label}-${index}`}
                      last={index === subtitleBadges.length - 1}
                    >
                      {badge.label}
                    </Styled.SubtitleBadge>
                  ))}
                </Styled.SubtitleBadgeWrapper>
              ) : null}
            </>
          )}
          {children}
        </Styled.Content>
        {!!footer && <Styled.Footer>{footer}</Styled.Footer>}
      </Styled.Card>
    </ConditionalWrapper>
  );
};
